import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useCustomers } from "../../context/CustomerContext";

const PER_PAGE = 100;

const CustomerList = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { customers, fetchCustomers } = useCustomers();

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("both");
    const [loadingId, setLoadingId] = useState(null);
    const [page, setPage] = useState(1);
    const [editData, setEditData] = useState(null);

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter]);

    if (!customers || customers.length === 0) {
        return <div className="p-4 text-gray-600">No customers found.</div>;
    }

    // Filtering
    const filtered = customers.filter((c) => {
        const term = search.toLowerCase();

        const matchesSearch =
            c.name?.toLowerCase().includes(term) ||
            c.email?.toLowerCase().includes(term) ||
            c.phone?.toLowerCase().includes(term);

        const matchesStatus =
            statusFilter === "both"
                ? true
                : statusFilter === "active"
                    ? c.status === 1
                    : c.status === 0;

        return matchesSearch && matchesStatus;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filtered.length / PER_PAGE);
    const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const handleToggleStatus = async (c) => {
        toast.dismiss();

        toast.info(
            <div>
                <p>
                    Are you sure you want to set{" "}
                    <strong>{c.name}</strong> as{" "}
                    {c.status === 1 ? "Inactive" : "Active"}?
                </p>

                <div className="flex gap-2 mt-2">
                    <button
                        className="px-3 py-1 bg-red-600 text-white rounded"
                        onClick={async () => {
                            try {
                                setLoadingId(c.id);

                                await axios.put(`${baseUrl}/customer/toggle-status`, {
                                    customer_id: c.id,
                                });

                                toast.success("Status updated!");
                                fetchCustomers();
                            } catch (err) {
                                toast.error("Failed to update status!");
                            } finally {
                                setLoadingId(null);
                                toast.dismiss();
                            }
                        }}
                    >
                        Yes
                    </button>

                    <button
                        className="px-3 py-1 bg-gray-600 text-white rounded"
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            { autoClose: false }
        );
    };

    return (
        <div className="p-4 bg-white shadow-lg rounded-xl border">
            <h2 className="text-xl font-semibold mb-4">Customer List</h2>

            {/* Search + Filters */}
            <div className="flex items-center gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-[420px] p-2 text-sm border rounded-md focus:ring-blue-500 focus:ring"
                />

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 text-sm border rounded-md focus:ring-blue-500 focus:ring"
                >
                    <option value="both">Both</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border rounded-lg text-sm">
                    <thead className="bg-gray-100 text-center">
                        <tr>
                            <th className="px-3 py-2 border">ID</th>
                            <th className="px-3 py-2 border">Name</th>
                            <th className="px-3 py-2 border">Email</th>
                            <th className="px-3 py-2 border">Phone</th>
                            <th className="px-3 py-2 border">Address</th>
                            <th className="px-3 py-2 border">Status</th>
                            <th className="px-3 py-2 border">Source</th>
                            <th className="px-3 py-2 border">Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {paginated.map((c) => (
                            <tr key={c.id} className="text-center hover:bg-gray-50">
                                <td className="px-3 py-2 border">{c.id}</td>
                                <td className="px-3 py-2 border">{c.name}</td>
                                <td className="px-3 py-2 border">{c.email || "N/A"}</td>
                                <td className="px-3 py-2 border">{c.phone || "N/A"}</td>
                                <td className="px-3 py-2 border">{c.address || "N/A"}</td>

                                {/* Status */}
                                <td className="px-3 py-2 border">
                                    <span
                                        className={`px-3 py-1 rounded-full text-xs font-semibold ${c.status === 1
                                            ? "bg-green-100 !text-green-700"
                                            : "bg-red-100 text-red-600"
                                            }`}
                                    >
                                        {c.status === 1 ? "Active" : "Inactive"}
                                    </span>
                                </td>

                                <td className="px-3 py-2 border">{c.source || "N/A"}</td>

                                <td className="px-3 py-2 border flex flex-col gap-2">
                                    {/* Toggle Status */}
                                    <button
                                        onClick={() => handleToggleStatus(c)}
                                        disabled={loadingId === c.id}
                                        className={`px-3 py-2 font-semibold rounded hover:shadow-md border text-xs bg-white
                                            ${c.status === 1
                                                ? "text-red-500 hover:text-red-600"
                                                : "text-green-500 hover:text-green-600"
                                            } 
                                            ${loadingId === c.id && "opacity-50 cursor-wait"}`}
                                    >
                                        {loadingId === c.id ? "Updating..." : c.status === 1 ? "Set Inactive" : "Set Active"}
                                    </button>

                                    {/* Edit Button */}
                                    <button
                                        onClick={() => setEditData(c)}
                                        className="px-3 py-2 font-semibold rounded hover:shadow-md border text-xs bg-white hover:bg-gray-100 text-blue-500 hover:text-blue-600"
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {paginated.length === 0 && (
                            <tr>
                                <td
                                    colSpan="8"
                                    className="p-4 text-gray-500 text-center font-semibold"
                                >
                                    No matching customers found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gapx-3 py-2 mt-4">
                <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-indigo-100"
                >
                    Prev
                </button>

                {[...Array(totalPages)].map((_, i) => {
                    const num = i + 1;
                    return (
                        <button
                            key={num}
                            onClick={() => setPage(num)}
                            className={`px-3 py-1 rounded text-sm border transition 
                                        ${page === num
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "hover:bg-indigo-100"
                                }`}
                        >
                            {num}
                        </button>
                    );
                })}

                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-indigo-100"
                >
                    Next
                </button>
            </div>

            {editData && (
                <div className="fixed inset-0 bg-black/40 bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-md w-[380px]">

                        <h3 className="text-lg font-semibold mb-3">Edit Customer</h3>

                        {/* Name */}
                        <input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="w-full p-2 border rounded mb-2"
                            placeholder="Name"
                        />

                        {/* Email */}
                        <input
                            value={editData.email || ""}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            className="w-full p-2 border rounded mb-2"
                            placeholder="Email (Optional)"
                        />

                        {/* Phone */}
                        <input
                            value={editData.phone || ""}
                            onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                            className="w-full p-2 border rounded mb-2"
                            placeholder="Phone"
                        />

                        {/* Address */}
                        <input
                            value={editData.address || ""}
                            onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                            className="w-full p-2 border rounded mb-2"
                            placeholder="Address (Optional)"
                        />

                        {/* Source */}
                        <select
                            value={editData.source || ""}
                            onChange={(e) => setEditData({ ...editData, source: e.target.value })}
                            className="w-full p-2 border rounded mb-2"
                        >
                            <option value="" disabled>Select Source</option>
                            <option value="Web Portal">Web Portal</option>
                            <option value="Alice Receptions">Alice Receptions</option>
                            <option value="Alice Conference">Alice Conference</option>
                        </select>

                        <div className="flex justify-end gap-2 mt-3">
                            <button
                                className="px-3 py-1 bg-gray-300 rounded"
                                onClick={() => setEditData(null)}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-3 py-1 bg-blue-600 text-white rounded"
                                onClick={async () => {
                                    try {
                                        await axios.put(
                                            `${baseUrl}/update-customer/${editData.id}`,
                                            editData
                                        );

                                        toast.success("Customer updated!");
                                        fetchCustomers();
                                        setEditData(null);

                                    } catch (err) {
                                        toast.error(err?.data?.error || err?.response?.data?.error || "Failed to update customer");
                                    }
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerList;
